import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useMutation, useQuery, gql } from "@apollo/client";
import { format } from "date-fns";
import { toast } from "@/components/ui/toast";

// TypeScript interfaces
interface CoordinatedCareNote {
  id: string;
  serviceType: string;
  placeOfService: string;
  procedurelength: string;
  noteEntryDate: string;
  procedurenote: string;
  procedureEnteredBy: string;
  entrydate: string;
  entryby: string;
}

interface EditCoordinatedCareProps {
  isOpen: boolean;
  onClose: () => void;
  coordinatedCareData: CoordinatedCareNote | null;
  user: {
    fullname: string;
  };
}

const EDIT_COORDINATED_CARE_NOTE = gql`
  mutation EditCoordinatedCareNote($noteId: Int!, $note: AddCoordinatedCareNoteInput!) {
    editCoordinatedCareNote(noteId: $noteId, note: $note) {
      message
    }
  }
`;

// Service Type options (static as defined in PHP)
const SERVICE_TYPE_OPTIONS = [
  { value: "none", label: "-- Select type --" },
  { value: "Housing", label: "Housing" },
  { value: "Community", label: "Community" },
  { value: "Pre-release", label: "Pre-release" },
  { value: "Psychiatry", label: "Psychiatry" },
  { value: "Nursing", label: "Nursing" },
  { value: "ECM", label: "ECM" }
];

// Place of Service options (static as defined in PHP)
const PLACE_OF_SERVICE_OPTIONS = [
  { value: "none", label: "-- Select an option --" },
  { value: "home", label: "Home" },
  { value: "office", label: "Office" },
  { value: "telehealth_home", label: "Telehealth in pt home" },
  { value: "telehealth_outside", label: "Telehealth outside pt home" }
];

export const EditCoordinateCare: React.FC<EditCoordinatedCareProps> = ({
  isOpen,
  onClose,
  coordinatedCareData,
  user,
}) => {

  const [formData, setFormData] = useState({
    service_type: "",
    place_of_service: "",
    procedurelength: "",
    note_entry_date: "",
    procedurenote: "",
    procedureentery: "",
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const [editCoordinatedCareNote, { loading: updateLoading }] = useMutation(EDIT_COORDINATED_CARE_NOTE);

  useEffect(() => {
    if (coordinatedCareData) {
      setFormData({
        service_type: coordinatedCareData.serviceType || "",
        place_of_service: coordinatedCareData.placeOfService || "",
        procedurelength: coordinatedCareData.procedurelength || "",
        note_entry_date: coordinatedCareData.noteEntryDate || format(new Date(), "yyyy-MM-dd"),
        procedurenote: coordinatedCareData.procedurenote || "",
        procedureentery: coordinatedCareData.procedureEnteredBy || user?.fullname || "",
      });
    }
  }, [coordinatedCareData, user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.place_of_service) {
      newErrors.place_of_service = "Place of Service must be selected";
    }

    if (!formData.procedurelength) {
      newErrors.procedurelength = "Procedure length must be selected";
    }

    if (!formData.procedurenote.trim()) {
      newErrors.procedurenote = "Procedure note is required";
    }

    if (!formData.procedureentery) {
      newErrors.procedureentery = "Entered by must be filled";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!coordinatedCareData) {
      toast.error('No coordinated care data available');
      return;
    }

    try {
      await editCoordinatedCareNote({
        variables: {
          noteId: parseInt(coordinatedCareData.id, 10),
          note: {
            service_type: formData.service_type,
            place_of_service: formData.place_of_service,
            procedurelength: formData.procedurelength,
            note_entry_date: formData.note_entry_date,
            procedurenote: formData.procedurenote,
            procedureentery: formData.procedureentery
          },
        },
      });

      onClose();
    } catch (error) {
      console.error("Error updating Coordinated Care note:", error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[90vw] max-w-none max-h-[90vh] overflow-y-auto" style={{ width: '90vw', minWidth: '1200px' }}>
        <DialogHeader>
          <DialogTitle className="text-center text-2xl font-bold">
            Edit Coordinated Care Note
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="service_type">Service Type</Label>
              <Select 
                onValueChange={(value) => handleSelectChange("service_type", value)}
                value={formData.service_type}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select service type" />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="place_of_service">Place of Service</Label>
              <Select 
                onValueChange={(value) => handleSelectChange("place_of_service", value)}
                value={formData.place_of_service}
              >
                <SelectTrigger className={errors.place_of_service ? "border-red-500" : ""}>
                  <SelectValue placeholder="Select place of service" />
                </SelectTrigger>
                <SelectContent>
                  {PLACE_OF_SERVICE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.place_of_service && <p className="text-sm text-red-500">{errors.place_of_service}</p>}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="procedurelength">Procedure Length</Label>
              <Select
                onValueChange={(value) => handleSelectChange("procedurelength", value)}
                value={formData.procedurelength}
              >
                <SelectTrigger className={errors.procedurelength ? "border-red-500" : ""}>
                  <SelectValue placeholder="Select procedure length" />
                </SelectTrigger>
                <SelectContent>
                  {PROCEDURE_LENGTH_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.procedurelength && <p className="text-sm text-red-500">{errors.procedurelength}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="note_entry_date">Note Date</Label>
              <Input
                type="date"
                id="note_entry_date"
                name="note_entry_date"
                value={formData.note_entry_date}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="procedureentery">Entered By</Label>
              <Input
                id="procedureentery"
                name="procedureentery"
                value={formData.procedureentery}
                onChange={handleInputChange}
                className={errors.procedureentery ? "border-red-500" : ""}
                required
              />
              {errors.procedureentery && <p className="text-sm text-red-500">{errors.procedureentery}</p>}
            </div>
          </div>


          <div className="space-y-2">
            <Label htmlFor="procedurenote">Procedure Note</Label>
            <Textarea
              id="procedurenote"
              name="procedurenote"
              value={formData.procedurenote}
              onChange={handleInputChange}
              placeholder="Enter your note here"
              className={`min-h-[100px] ${errors.procedurenote ? "border-red-500" : ""}`}
              required
            />
            {errors.procedurenote && <p className="text-sm text-red-500">{errors.procedurenote}</p>}
          </div>

          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button type="submit" disabled={updateLoading}>
              {updateLoading ? "Saving..." : "Update Note"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// These match the procedure lengths from the PHP file
const PROCEDURE_LENGTH_OPTIONS = [
  { value: "15", label: "15 mins" },
  { value: "30", label: "30 mins" },
  { value: "45", label: "45 mins" },
  { value: "60", label: "60 mins" },
  { value: "75", label: "1hour 15mins" },
  { value: "90", label: "1hour 30mins" },
  { value: "105", label: "1hour 45mins" },
  { value: "120", label: "2hours" },
  { value: "150", label: "2hours 30mins" },
  { value: "180", label: "3hours" },
  { value: "210", label: "3hours 30mins" },
  { value: "240", label: "4hours" }
];
